import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Badge,
  Box,
  CircularProgress,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Paper,
  Popper,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';

// importação do projeto
import MainCard from '../../../../components/MainCard';
import IconButton from '../../../../components/@extended/IconButton';
import Transitions from '../../../../components/@extended/Transitions';
import { ThemeMode } from '../../../../config';
import { marcarNotificacao, marcarTodasComoLidas, useGetNotificacoes } from '../../../../api/notificacoes';
import { MAX_NOTIFICACOES_NO_SINO, idsNaoLidas } from '../../../../utils/notificacoes';
import ConfirmarMarcarTodasDialog, { mensagemSucessoEmLote } from '../../../../pages/apps/notificacoes/ConfirmarMarcarTodasDialog';
import DetalheNotificacao from '../../../../pages/apps/notificacoes/DetalheNotificacao';

// ativos
import { BellOutlined, CheckCircleOutlined } from '@ant-design/icons';

// estilos sx
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const MSG_VAZIO = 'Nenhuma notificação.';
const MSG_CARREGANDO = 'Carregando notificações...';
const MSG_ERRO_MARCAR = 'Não foi possível marcar a notificação como lida.';

// Estados do conteúdo do popper — mutuamente exclusivos.
const ESTADO = {
  SESSAO_EXPIRADA: 'sessaoExpirada',
  LISTA: 'lista',
  CARREGANDO: 'carregando',
  ERRO: 'erro',
  VAZIO: 'vazio'
};

// Havendo lista, ela prevalece sobre carregando e erro: a atualização e a falha do polling são silenciosas (a falha vira só um aviso
// acima da lista). A sessão expirada vem primeiro: a lista, se existir, é velha e o hook parou de buscar.
const obterEstadoPopper = ({ sessaoExpirada, isLoading, error, total }) => {
  if (sessaoExpirada) return ESTADO.SESSAO_EXPIRADA;
  if (total > 0) return ESTADO.LISTA;
  if (isLoading) return ESTADO.CARREGANDO;
  if (error) return ESTADO.ERRO;
  return ESTADO.VAZIO;
};

// ==============================|| CABEÇALHO DE CONTEÚDO - NOTIFICAÇÃO ||============================== //
//
// A API só tem título, mensagem, situação de leitura e link: sem data, sem tipo. Por isso o item mostra o título numa linha e a mensagem
// na outra, sem hora, e o avatar usa sempre o sino (`tom` "primary"). A lista já chega com as não lidas primeiro e o popper mostra as
// primeiras MAX_NOTIFICACOES_NO_SINO; o badge conta todas as não lidas, não só as exibidas. O resto fica na página de listagem.

const Notification = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

  const { notificacoes, naoLidas, isLoading, error, sessaoExpirada, recarregar, definirLeituraLocal } = useGetNotificacoes();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [detalhe, setDetalhe] = useState({ open: false, id: null, notificacao: null });
  // Os ids ficam congelados quando a confirmação abre: o que o usuário confirma é exatamente o que é enviado.
  const [confirmacao, setConfirmacao] = useState({ open: false, ids: [] });
  const [marcandoTodas, setMarcandoTodas] = useState(false);

  const estado = obterEstadoPopper({ sessaoExpirada, isLoading, error, total: notificacoes.length });
  const exibidas = notificacoes.slice(0, MAX_NOTIFICACOES_NO_SINO);

  // Abrir o sino busca de novo (D-N2), além do polling de 60 s do hook.
  const handleToggle = () => {
    if (!open) recarregar();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // Otimista: só é seguro para `true`, porque o efeito no servidor é imediato e o PUT não desmarca. A falha desfaz só o estado local.
  const marcarComoLida = async (notificacao) => {
    if (notificacao.lida) return;

    const { id } = notificacao;
    definirLeituraLocal(id, true);

    try {
      await marcarNotificacao(id);
    } catch (e) {
      definirLeituraLocal(id, false);
      enqueueSnackbar(e?.message || MSG_ERRO_MARCAR, { variant: 'error' });
    }
  };

  // Clicar no item = fechar o popper, abrir o detalhe e marcar como lida (se ainda não estiver). Mesma regra da página de listagem.
  const handleAbrirItem = (notificacao) => {
    setOpen(false);
    setDetalhe({ open: true, id: notificacao.id, notificacao });
    marcarComoLida(notificacao);
  };

  // Mantém a notificação no estado ao fechar: o conteúdo não some antes de a animação de saída do Dialog terminar.
  const fecharDetalhe = () => setDetalhe((atual) => ({ ...atual, open: false }));

  const irParaRegistro = (rota) => {
    fecharDetalhe();
    navigate(rota);
  };

  const handleVerTodas = () => {
    setOpen(false);
    navigate('/notificacoes/lista');
  };

  const abrirConfirmacaoMarcarTodas = () => {
    if (marcandoTodas) return;
    setOpen(false);
    setConfirmacao({ open: true, ids: idsNaoLidas(notificacoes) });
  };

  const fecharConfirmacao = () => setConfirmacao((atual) => ({ ...atual, open: false }));

  const confirmarMarcarTodas = async () => {
    fecharConfirmacao();
    setMarcandoTodas(true);

    try {
      const resumo = await marcarTodasComoLidas(confirmacao.ids);

      if (resumo.mensagem) {
        enqueueSnackbar(resumo.mensagem, { variant: 'error' });
      } else {
        enqueueSnackbar(mensagemSucessoEmLote(resumo.ok), { variant: 'success' });
      }
    } catch (e) {
      enqueueSnackbar(e?.message || MSG_ERRO_MARCAR, { variant: 'error' });
    } finally {
      setMarcandoTodas(false);
    }
  };

  // O detalhe acompanha a lista viva (o chip vira "Lida" ao abrir); o item guardado só serve se ele sumir da lista.
  const notificacaoDoDetalhe = notificacoes.find((n) => n.id === detalhe.id) ?? detalhe.notificacao;

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <Tooltip title="Notificações">
        <IconButton
          color="secondary"
          variant="light"
          sx={{ color: 'text.primary', bgcolor: open ? iconBackColorOpen : 'transparent' }}
          aria-label="abrir notificações"
          ref={anchorRef}
          aria-controls={open ? 'notificacoes-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Badge badgeContent={naoLidas} color="error" max={99}>
            <BellOutlined />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [matchesXs ? -5 : 0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: '100%',
                minWidth: 340,
                maxWidth: 420,
                [theme.breakpoints.down('sm')]: {
                  minWidth: 285,
                  maxWidth: 285
                }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notificações"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {naoLidas > 0 && !sessaoExpirada && (
                        <Tooltip title="Marcar tudo como lido">
                          <IconButton color="success" size="small" onClick={abrirConfirmacaoMarcarTodas}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      maxHeight: 420,
                      overflowY: 'auto',
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX
                      }
                    }}
                  >
                    {estado === ESTADO.LISTA && (
                      <>
                        {error && (
                          <Typography variant="caption" color="text.secondary" role="status" sx={{ display: 'block', px: 2, py: 0.75 }}>
                            {error} Exibindo a última lista carregada.
                          </Typography>
                        )}
                        {exibidas.map((item) => (
                          <Box key={item.id}>
                            <ListItemButton selected={!item.lida} onClick={() => handleAbrirItem(item)}>
                              <ListItemAvatar>
                                {/* Sem tipo na API: sempre o sino, no tom do item. */}
                                <Avatar sx={{ color: `${item.tom}.main`, bgcolor: `${item.tom}.lighter` }}>
                                  <BellOutlined />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography variant="subtitle1" noWrap>
                                    {item.destaque}
                                  </Typography>
                                }
                                secondary={item.texto}
                              />
                            </ListItemButton>
                            <Divider />
                          </Box>
                        ))}
                      </>
                    )}

                    {estado === ESTADO.CARREGANDO && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 2, py: 3 }}>
                        <CircularProgress size={20} />
                        <Typography color="text.secondary">{MSG_CARREGANDO}</Typography>
                      </Box>
                    )}

                    {(estado === ESTADO.ERRO || estado === ESTADO.SESSAO_EXPIRADA) && (
                      <Typography role="alert" color="text.secondary" align="center" sx={{ px: 2, py: 3 }}>
                        {error}
                      </Typography>
                    )}

                    {estado === ESTADO.VAZIO && (
                      <Typography color="text.secondary" align="center" sx={{ px: 2, py: 3 }}>
                        {MSG_VAZIO}
                      </Typography>
                    )}

                    <ListItemButton sx={{ textAlign: 'center', py: `${12}px !important` }} onClick={handleVerTodas}>
                      <ListItemText
                        primary={
                          <Typography variant="h6" color="primary">
                            Ver todas
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      <DetalheNotificacao open={detalhe.open} notificacao={notificacaoDoDetalhe} onClose={fecharDetalhe} onIrParaRegistro={irParaRegistro} />

      <ConfirmarMarcarTodasDialog
        open={confirmacao.open}
        quantidade={confirmacao.ids.length}
        onConfirm={confirmarMarcarTodas}
        onClose={fecharConfirmacao}
      />
    </Box>
  );
};

export default Notification;
